import React, { useState, useEffect } from 'react';
import { useGlobalStorageContext } from '../contexts/GlobalStorageContext';
import { useLanguage } from '../contexts/LanguageContext';
import { useTheme } from '../contexts/ThemeContext';
import { GOOGLE_MAPS_API_KEY } from '../config/googleMaps';
import './QiblahPage.css';

declare global {
  interface Window {
    google: any;
  }
}

const KAABA_LAT = 21.4225;
const KAABA_LNG = 39.8262;

const calculateQiblahDirection = (lat: number, lng: number): number => {
  const toRad = (deg: number) => (deg * Math.PI) / 180;
  const toDeg = (rad: number) => (rad * 180) / Math.PI;

  const lat1 = toRad(lat);
  const lng1 = toRad(lng);
  const lat2 = toRad(KAABA_LAT);
  const lng2 = toRad(KAABA_LNG);

  const dLng = lng2 - lng1;
  const y = Math.sin(dLng) * Math.cos(lat2);
  const x = Math.cos(lat1) * Math.sin(lat2) - Math.sin(lat1) * Math.cos(lat2) * Math.cos(dLng);
  
  let bearing = toDeg(Math.atan2(y, x));
  return (bearing + 360) % 360;
};

const CompassView: React.FC<{ manualLocation: any; colors: any; t: any }> = ({ manualLocation, colors, t }) => {
  const [heading, setHeading] = useState(0);
  const [qiblahDirection, setQiblahDirection] = useState(0);

  useEffect(() => {
    if (manualLocation) {
      const direction = calculateQiblahDirection(manualLocation.latitude, manualLocation.longitude);
      setQiblahDirection(direction);
    }
  }, [manualLocation]);

  useEffect(() => {
    if ('DeviceOrientationEvent' in window) {
      const handleOrientation = (event: DeviceOrientationEvent) => {
        if (event.alpha !== null) {
          setHeading(360 - event.alpha);
        }
      };

      window.addEventListener('deviceorientation', handleOrientation);
      return () => window.removeEventListener('deviceorientation', handleOrientation);
    }
  }, []);

  const rotation = qiblahDirection - heading;

  return (
    <>
      <div className="compass-container">
        <div className="compass" style={{ transform: `rotate(${rotation}deg)` }}>
          <div className="compass-arrow" style={{ backgroundColor: colors.tint }}>
            <div className="arrow-head"></div>
          </div>
          <div className="compass-center"></div>
        </div>
      </div>

      <div className="info-card" style={{ backgroundColor: colors.cardBackground, borderColor: colors.border }}>
        <div className="info-item">
          <span className="info-label">Qiblah Direction:</span>
          <span className="info-value">{qiblahDirection.toFixed(1)}°</span>
        </div>
        <div className="info-item">
          <span className="info-label">Current Heading:</span>
          <span className="info-value">{heading.toFixed(1)}°</span>
        </div>
        <div className="info-item">
          <span className="info-label">Location:</span>
          <span className="info-value">{manualLocation.city}</span>
        </div>
      </div>
    </>
  );
};

const MapView: React.FC<{ manualLocation: any; colors: any; isDarkMode: boolean }> = ({ manualLocation, colors, isDarkMode }) => {
  const [qiblahDirection, setQiblahDirection] = useState(0);

  useEffect(() => {
    if (manualLocation) {
      const direction = calculateQiblahDirection(manualLocation.latitude, manualLocation.longitude);
      setQiblahDirection(direction);
    }
  }, [manualLocation]);

  useEffect(() => {
    if (!window.google || !manualLocation) return;

    const map = new window.google.maps.Map(document.getElementById('map')!, {
      zoom: 17,
      center: { lat: manualLocation.latitude, lng: manualLocation.longitude },
      mapTypeId: isDarkMode ? 'hybrid' : 'roadmap',
      disableDefaultUI: false,
      zoomControl: true,
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: false,
    });

    new window.google.maps.Marker({
      position: { lat: manualLocation.latitude, lng: manualLocation.longitude },
      map,
      title: 'Your Location',
      icon: {
        path: window.google.maps.SymbolPath.CIRCLE,
        scale: 8,
        fillColor: '#3b82f6',
        fillOpacity: 1,
        strokeColor: 'white',
        strokeWeight: 2,
      }
    });

    new window.google.maps.Marker({
      position: { lat: KAABA_LAT, lng: KAABA_LNG },
      map,
      title: 'Makkah',
      icon: {
        path: window.google.maps.SymbolPath.CIRCLE,
        scale: 10,
        fillColor: '#059669',
        fillOpacity: 1,
        strokeColor: 'white',
        strokeWeight: 2,
      }
    });

    new window.google.maps.Polyline({
      path: [
        { lat: manualLocation.latitude, lng: manualLocation.longitude },
        { lat: KAABA_LAT, lng: KAABA_LNG }
      ],
      geodesic: true,
      strokeColor: '#059669',
      strokeOpacity: 0.8,
      strokeWeight: 3,
      map,
    });

    new window.google.maps.Marker({
      position: { lat: manualLocation.latitude, lng: manualLocation.longitude },
      map,
      icon: {
        path: window.google.maps.SymbolPath.FORWARD_CLOSED_ARROW,
        scale: 5,
        fillColor: '#ef4444',
        fillOpacity: 1,
        strokeColor: 'white',
        strokeWeight: 2,
        rotation: qiblahDirection
      },
      zIndex: 1000,
    });
  }, [manualLocation, isDarkMode, qiblahDirection]);

  return (
    <>
      <div className="map-info" style={{ backgroundColor: colors.cardBackground, borderColor: colors.border }}>
        <div className="bearing-display" style={{ color: colors.tint }}>{qiblahDirection.toFixed(1)}°</div>
        <div className="bearing-label" style={{ color: colors.muted }}>Qiblah Direction</div>
      </div>
      <div id="map" className="map-container"></div>
    </>
  );
};

export const QiblahPage: React.FC = () => {
  const { t } = useLanguage();
  const { colors, isDarkMode } = useTheme();
  const { manualLocation } = useGlobalStorageContext();
  const [activeTab, setActiveTab] = useState<'compass' | 'map'>('compass');
  const [mapsLoaded, setMapsLoaded] = useState(false);

  useEffect(() => {
    if (activeTab === 'map' && !mapsLoaded && GOOGLE_MAPS_API_KEY) {
      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}`;
      script.async = true;
      script.onload = () => setMapsLoaded(true);
      document.head.appendChild(script);
    }
  }, [activeTab, mapsLoaded]);

  return (
    <div className="qiblah-page" style={{ backgroundColor: colors.background, color: colors.text }}>
      <div className="qiblah-tabs" style={{ backgroundColor: colors.cardBackground, borderColor: colors.border }}>
        <button
          className={`qiblah-tab ${activeTab === 'compass' ? 'active' : ''}`}
          onClick={() => setActiveTab('compass')}
          style={{
            color: activeTab === 'compass' ? colors.tint : colors.muted,
            borderBottomColor: activeTab === 'compass' ? colors.tint : 'transparent'
          }}
        >
          {t.qiblah.compass}
        </button>
        <button
          className={`qiblah-tab ${activeTab === 'map' ? 'active' : ''}`}
          onClick={() => setActiveTab('map')}
          style={{
            color: activeTab === 'map' ? colors.tint : colors.muted,
            borderBottomColor: activeTab === 'map' ? colors.tint : 'transparent'
          }}
        >
          {t.qiblah.direction}
        </button>
      </div>

      {activeTab === 'compass' ? (
        <CompassView manualLocation={manualLocation} colors={colors} t={t} />
      ) : (
        <MapView manualLocation={manualLocation} colors={colors} isDarkMode={isDarkMode} />
      )}
    </div>
  );
};
